<template>
    <div class="campaign-edit__header dark-scrollbar">
        <SectionNav />

        <div class="campaign-edit__header-action-wrapper left">
            <Select v-if="!sandboxMode" v-model="currentLanguage" class="campaign-edit__header-action-select">
                <Option v-for="lang in campaignLanguages" :key="lang" :value="lang">
                    {{ languageLabel(lang) }}
                </Option>
            </Select>
            <AddLanguage v-if="!sandboxMode" />
            <AddTemplates
                v-if="!sandboxMode && $auth.userCan($auth.Actions.CanManageMasterTemplates, { clientId }, false)"
                button-label="Add Template"
                button-data-testid="editor-header__add-template-button"
                button-class="campaign-edit__header-action-btn campaign-edit__header-action-btn--add-template"
            />
            <Button
                v-if="!sandboxMode && $auth.userCan($auth.Actions.CanManageMediaItems)"
                class="campaign-edit__header-action-btn campaign-edit__header-action-btn--add-assets"
                data-testid="editor-header__add-assets-button"
                @click="showAssetsModal"
            >
                Add Assets
            </Button>
            <Button
                v-if="!sandboxMode && clientHasConnectEnabled && !hasMetaPlatform"
                class="campaign-edit__header-action-btn campaign-edit__header-action-btn--add-platform"
                data-testid="editor-header__add-platform-button"
                @click="addPlatform"
            >
                Platform Metadata
            </Button>
            <ImportExportContent :sandbox-mode="sandboxMode" />
        </div>
        <div class="campaign-edit__header-action-wrapper right">
            <div class="save-changes">
                <Button
                    class="campaign-edit__header-action-btn"
                    data-testid="editor-header__cancel-button"
                    :disabled="!localOverwritesMade || isCheckingForOverwrites"
                    @click="openCancelChangesModal"
                >
                    Cancel all
                </Button>
                <Button
                    class="campaign-edit__header-action-btn campaign-edit__header-action-btn--primary"
                    type="primary"
                    data-testid="editor-header__create-button"
                    :disabled="!localOverwritesMade || isCheckingForOverwrites"
                    :loading="isCheckingForOverwrites"
                    @click="openSaveChangesModal"
                >
                    Save all
                </Button>
            </div>
        </div>
        <alert-modal
            v-model="cancelChangesModal"
            header="Cancel changes"
            title="Are you sure?"
            content="Would you like to discard your changes?"
            ok-text="Discard"
            :on-ok="cancelChanges"
        />
        <alert-modal
            v-model="saveChangesModal"
            header="Save changes"
            title="Are you sure?"
            :content="saveChangesModalContent"
            ok-text="Save"
            :warning="true"
            :on-ok="saveChanges"
        />
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import AlertModal from "@/components/Modal/Alert.vue";
import SectionNav from "@/components/SectionNav.vue";
import AddLanguage from "@/components/Campaign/EditorHeader/AddLanguage.vue";
import ImportExportContent from "@/components/Campaign/EditorHeader/ImportExportContent.vue";
import AddTemplates from "@/components/Campaign/EditorHeader/AddTemplates.vue";

import { localeToFriendlyName, type LanguageCode } from "@/utils/languages";

import { OpenDcCampaignType } from "hoxton-shared/enums/opendc";
import { EditorAction } from "@/store/modules/editor";
import { MediaLibraryAction } from "@/store/modules/mediaLibrary";
import { campaignDefaultLanguage } from "@/store/modules/campaign";
import attachPlatformEditablesToCampaign from "@/apollo/mutations/v2/AttachPlatformEditablesToCampaign.gql";
import canOverwriteEditables from "@/apollo/queries/CanOverwriteEditables.gql";
import type { Err } from "@/types/types";
import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";

export default defineComponent({
    name: "EditorHeader",
    components: { AlertModal, SectionNav, AddLanguage, ImportExportContent, AddTemplates },

    props: {
        hasMetaPlatform: {
            type: Boolean,
            default: false
        }
    },

    emits: ["addPlatform", "saveChanges", "changeAdType", "publishToPlatform"],

    setup() {
        const ga4 = useGA4Tracking();

        return {
            ga4
        };
    },

    data() {
        return {
            cancelChangesModal: false,
            saveChangesModal: false,
            isCheckingForOverwrites: false,
            showOverrideAlert: false
        };
    },

    computed: {
        campaignLanguages() {
            return this.$store.state.campaign.languages;
        },
        clientId() {
            return this.$route.params.clientId as string;
        },
        campaignId() {
            return this.$route.params.campaignId as string;
        },
        localOverwritesMade() {
            return (
                this.$store.state.editor.localEditableOverwrites.length > 0 ||
                this.$store.state.editor.removedOverwriteIds.length > 0
            );
        },
        currentLanguage: {
            get() {
                return this.$store.state.editor.selectedLanguage;
            },
            set(val: LanguageCode) {
                this.$store.dispatch(EditorAction.SelectLanguage, val);
            }
        },
        clientHasConnectEnabled() {
            if (this.$store.state.ui.currentClient) {
                return this.$store.state.ui.currentClient.connectModuleEnabled;
            }

            return false;
        },
        saveChangesModalContent() {
            return `Your content edits will be applied to the ads in this campaign. This cannot be undone.
            <br>Once saved, you can preview your ads in the Creative Library.${
                this.showOverrideAlert
                    ? "<br><br><b>Your existing score may no longer reflect the final content.</b>"
                    : ""
            }`;
        },

        hasCreativeXEnabled() {
            return this.$store.state.ui.currentClient?.creativeXPlatformSettings?.enabled ?? false;
        },

        hasSataliaEnabled() {
            return this.$store.state.ui.currentClient?.sataliaPlatformSettings?.enabled ?? false;
        },

        sandboxMode() {
            return this.$store.state.editor.isSandboxMode;
        },

        publishingPlatform() {
            const campaignType = this.$store.state.campaign.opendcPlatformSettings?.campaignType;
            return campaignType === OpenDcCampaignType.tiktokVideo ? "tiktok" : "facebook";
        }
    },

    watch: {
        adType(val) {
            this.$emit("changeAdType", val);
        }
    },

    created() {
        if (this.currentLanguage !== campaignDefaultLanguage) {
            this.currentLanguage = campaignDefaultLanguage;
        }
    },

    methods: {
        openCancelChangesModal() {
            this.cancelChangesModal = true;
        },

        cancelChanges() {
            this.$store.dispatch(EditorAction.ClearLocalChanges);
        },

        async openSaveChangesModal() {
            if (this.sandboxMode || (!this.hasCreativeXEnabled && !this.hasSataliaEnabled)) {
                this.saveChangesModal = true;

                return;
            }

            try {
                this.isCheckingForOverwrites = true;

                const editableGroupValueIds = new Set(this.$store.state.editor.removedOverwriteIds);

                this.$store.state.editor.localEditableOverwrites.forEach(editableOverwrite => {
                    editableOverwrite.overwrites.forEach(overwrite => {
                        editableGroupValueIds.add(...overwrite.editableGroupValueIds);
                    });
                });

                const { data } = await this.$apollo.query({
                    query: canOverwriteEditables,
                    variables: {
                        campaignId: this.campaignId,
                        editableGroupValueIds: Array.from(editableGroupValueIds)
                    },
                    fetchPolicy: "no-cache"
                });

                this.showOverrideAlert = !data?.canOverwriteEditables;
            } catch {
                this.$snackbar.error("Error checking for overwrites");
                this.showOverrideAlert = true;
            } finally {
                this.saveChangesModal = true;
                this.isCheckingForOverwrites = false;
            }
        },

        saveChanges() {
            this.$emit("saveChanges");
        },

        showAssetsModal() {
            this.$store.dispatch(MediaLibraryAction.Open, {});
        },

        languageLabel(locale: LanguageCode) {
            return localeToFriendlyName(locale);
        },

        async addPlatform() {
            try {
                await this.$apollo.mutate({
                    mutation: attachPlatformEditablesToCampaign,
                    variables: {
                        campaignId: this.campaignId,
                        publishingPlatform: this.publishingPlatform
                    }
                });
                this.ga4.trackClickEvent({
                    interactionObject: GA4.CLICK_INTERACTION_OBJECTS.BUTTON,
                    interactionText: GA4.CLICK_INTERACTION_TEXTS.ADD_PLATFORM_METADATA
                });
                this.$snackbar.success("Platform added.");
                this.$emit("publishToPlatform");
                const addPlatformButton = this.$el.querySelector(".campaign-edit__header-action-btn--add-platform");
                if (addPlatformButton) {
                    addPlatformButton.blur();
                }
            } catch (err) {
                this.$snackbar.error((err as Err).message);
            }
        }
    }
});
</script>

<style lang="scss">
@use "sass:color";
@use "@style/variables" as *;

.campaign-edit__header {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin: -20px 0 0 0;
    height: 60px;

    &-action-wrapper {
        flex: 1;
        display: flex;

        &.right {
            display: flex;
            justify-content: flex-end;
            margin-left: auto;
            white-space: nowrap;
        }
    }

    &-action-select {
        max-width: 175px;
    }

    &-action-btn,
    &-action-select .ivu-select-selection,
    &-action-select .ivu-select-dropdown {
        background: $cmp-dark-btn-bg-color;
        color: $cmp-dark-font-color;
        border: 1px solid $cmp-dark-btn-bg-color;
        margin-right: 5px;

        > span {
            display: block;
        }

        &[disabled] {
            opacity: 0.5;
            background: $cmp-dark-btn-bg-color;
            color: $cmp-dark-font-color;
            border: 1px solid $cmp-dark-btn-bg-color;

            &:hover {
                background: $cmp-dark-btn-bg-color;
                color: $cmp-dark-font-color;
                border: 1px solid $cmp-dark-btn-bg-color;
            }
        }

        &:hover {
            background: color.adjust($cmp-dark-btn-bg-color, $lightness: 10%);
            border: 1px solid color.adjust($cmp-dark-btn-bg-color, $lightness: 10%);
            color: $cmp-dark-font-color;
        }

        &--primary {
            background: $blue;
            border: 1px solid $blue;

            &[disabled] {
                background: $blue;
                border: 1px solid $blue;

                &:hover {
                    background: $blue;
                    border: 1px solid $blue;
                }
            }

            &:hover {
                background: color.adjust($blue, $lightness: 10%);
                border: 1px solid color.adjust($blue, $lightness: 10%);
            }
        }
    }

    &-action-select.ivu-select-single .ivu-select-selection .ivu-select-selected-value {
        padding-left: 15px;
        padding-right: 32px;
    }

    &-action-select .ivu-select-dropdown {
        margin-top: 1px;
        padding: 0;
        height: auto;

        &:hover {
            background: $cmp-dark-btn-bg-color;
        }

        .ivu-select-item {
            color: $cmp-dark-font-color;
            border-bottom: 1px solid $cmp-dark-border-color;
            padding: 10px;

            &:last-child {
                border-bottom: none;
            }

            &.ivu-select-item-selected {
                color: $cmp-dark-active;
            }

            &.ivu-select-item-focus {
                background: $cmp-dark-btn-bg-color;
            }

            &:hover {
                background: color.adjust($cmp-dark-btn-bg-color, $lightness: 10%);
            }
        }
    }
}
</style>
